<template>
  <div class="lp-options-group-wrapper pt-1 border-gray-medium">
    <p class="pl-1 m-0 font-weight-bold text-dark-muted text-uppercase">
      {{ label }}
    </p>
    <div
      :ref="(dropdown) => setSelectListRef(dropdown)"
      class="lp-options-group"
    >
      <li
        v-for="(option, optionIndex) in options"
        v-show="
          searchValue === undefined ||
          String(getOptionName(option)).toLowerCase().includes(searchValue)
        "
        :key="optionIndex"
        :ref="(el) => setOptionRef(el, optionIndex)"
        class="lp-select-option ly-col"
        :class="[
          wrappedValue[trackBy] === getOptionValue(option) &&
            'lp-select-option-selected',
        ]"
        :data-ua-multiple-id="uaLocatorOption"
        :data-ua-value="`${groupId}-${getOptionValue(option)}`"
        @click="
          () => {
            selectOption(optionIndex, groupId)
            toggleShow(option)
          }
        "
      >
        <div class="ly-row ly-start-center" :class="option.helper && 'mb-half'">
          <span
            v-if="typeof option[prefix] === 'string' && option[prefix]"
            class="lp-select-option-prefix"
            v-html="option[prefix]"
          />
          <component :is="option[prefix]" v-else />
          <slot name="option" :props="props" :option="option">
            <span class="lp-select-option-label">
              {{ getOptionName(option) }}
            </span>
          </slot>
          <span
            v-if="typeof option[suffix] === 'string' && option[suffix]"
            class="lp-select-option-suffix"
            v-html="option[suffix]"
          />
          <component :is="option[suffix]" v-else />
        </div>
        <p v-if="option.helper" v-html="option.helper" />
      </li>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toRef, inject, ref, computed, watchEffect, reactive, Ref } from 'vue'
import { useSelect } from '@/components/select/hooks'
import { TOption, TSelectProps } from '@/components/select/types'

import { CLASS_SELECTED_OPTION } from '@/components/select/constants'
import { TEmit } from '@/types/global'
import { TProps } from '@/types/global'

defineOptions({
  name: 'lp-options-group',
})

const emit = defineEmits(['update:model-value'])

interface Props {
  label?: string
  options: TOption[]
  groupId?: string | null
  labelBy?: string
  trackBy?: string
  disabled?: boolean
  uaLocatorOption?: string
  prefix?: string
  suffix?: string
  maxLabelLength?: string | number
}
const props = withDefaults(defineProps<Props>(), {
  label: 'All',
  options: () => [],
  groupId: null,
  labelBy: 'name',
  trackBy: 'value',
  disabled: false,
  uaLocatorOption: 'ua-optionsgroup-option',
  prefix: 'prefix',
  suffix: 'suffix',
  maxLabelLength: Infinity,
})

const options = toRef(props, 'options')
const toggleShow = inject('toggleShow', () => {}) as any
const emitHandlers = inject('emitHandlers', () => {}) as any
const wrapperValue = ref(inject('wrapperValue', null))
const modelGroupValue = ref(inject('modelGroupValue', null))
const groupIdMatch = computed(() => {
  return modelGroupValue.value ? props.groupId === modelGroupValue.value : true
})
const modelValue: Ref<TOption | null> = ref(null)
watchEffect(() => {
  modelValue.value = groupIdMatch.value ? wrapperValue.value : null
  if (modelValue.value) {
    const model = props.options.find(
      (x) => x[props.trackBy] === modelValue.value,
    )
    if (model) {
      emitHandlers.handleLabelChange(model[props.labelBy])
    }
  }
})

const {
  wrappedValue,
  getOptionName,
  getOptionValue,
  setOptionRef,
  indexFocusedOption,
  optionsRefs,
  show,
  handleEnterKey,
  setOpen,
  setSelectListRef,
  toggleDropdown,
  selectOption,
  handleKey,
} = useSelect(
  reactive({
    disabled: toRef(props, 'disabled'),
    modelValue,
    trackBy: props.trackBy,
    labelBy: props.labelBy,
    options: toRef(props, 'options'),
  } as TSelectProps),
  emit as TEmit,
  emitHandlers,
)

const searchValue = inject('searchEmitValue', '')
</script>

<style>
.lp .menu-pop-up .lp-options-group-wrapper {
  padding-right: 0;
  padding-left: 0;
}
</style>
